import { useCallback, useEffect, useMemo, useState } from 'react';
import theme from '../adminTheme';
import {
  guardarRanking,
  listarRanking,
  previsualizarRanking,
  quitarCargaRanking,
} from '../../../services/rankingsService';

const MOTIVOS = {
  blanqueo: 'un movimiento en blanco',
  solo_movimiento: 'only bench, only deadlift u only squat',
  sin_division: 'sin división de powerlifting',
  sin_categoria: 'sin categoría de peso',
  otro_pais: 'país distinto de Chile',
};

const EDAD = {
  subjunior: 'Sub-Junior',
  junior: 'Junior',
  open: 'Open',
  master_i: 'Master I',
  master_ii: 'Master II',
  master_iii: 'Master III',
  master_iv: 'Master IV',
  master_v: 'Master V',
};

const kg = (valor) => (valor == null || valor === '' ? '—' : valor);
const dots = (valor) => (valor == null || valor === '' ? '—' : Number(valor).toFixed(2));

const etiqueta = (valor) => {
  if (!valor) return '—';
  if (EDAD[valor]) return EDAD[valor];
  if (valor === 'tested') return 'Tested';
  if (valor === 'untested') return 'Untested';
  return valor.charAt(0).toUpperCase() + valor.slice(1);
};

const tituloGrupo = (grupo, tipo) => {
  const partes = [
    etiqueta(grupo.modalidad),
    grupo.genero,
  ];
  if (tipo === 'categoria') partes.push(etiqueta(grupo.categoria_edad));
  partes.push(etiqueta(grupo.tipo_competencia));
  if (tipo === 'categoria' && grupo.categoria_peso) partes.push(grupo.categoria_peso);
  return partes.filter(Boolean).join(' · ');
};

const RankingsView = () => {
  const anioActual = new Date().getFullYear();
  const [url, setUrl] = useState('');
  const [anioCarga, setAnioCarga] = useState(anioActual);
  const [ambito, setAmbito] = useState('nacional');
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [preview, setPreview] = useState(null);
  const [decisiones, setDecisiones] = useState({});
  const [popupConflictos, setPopupConflictos] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');

  const [anioVista, setAnioVista] = useState(anioActual);
  const [tipo, setTipo] = useState('overall');
  const [ranking, setRanking] = useState(null);
  const [filtros, setFiltros] = useState({
    modalidad: '',
    genero: '',
    categoria_edad: '',
    tipo_competencia: '',
    categoria_peso: '',
    buscar: '',
  });

  const cargarRanking = useCallback(async (anio, tipoRanking) => {
    const data = await listarRanking(anio, tipoRanking);
    setRanking(data);
    if (data.anio) setAnioVista(data.anio);
  }, []);

  useEffect(() => {
    cargarRanking(anioActual, 'overall').catch((e) => setError(e.message));
  }, [anioActual, cargarRanking]);

  const cambiarUrl = (valor) => {
    setUrl(valor);
    setPreview(null);
    setDecisiones({});
    setPopupConflictos(false);
    setResultado(null);
  };

  const previsualizar = async (event) => {
    event.preventDefault();
    setError('');
    setResultado(null);
    setCargando(true);
    try {
      const data = await previsualizarRanking(url.trim(), Number(anioCarga), ambito);
      setPreview(data);
      setDecisiones({});
      setPopupConflictos((data.conflictos || []).length > 0);
    } catch (e) {
      setPreview(null);
      setError(e.message);
    } finally {
      setCargando(false);
    }
  };

  const guardar = async () => {
    setError('');
    setGuardando(true);
    try {
      const data = await guardarRanking(
        url.trim(),
        Number(anioCarga),
        ambito,
        (preview?.conflictos || []).map((conflicto) => ({
          id: conflicto.id,
          claves: conflicto.personas.map((persona) => persona.clave_atleta),
          misma: decisiones[conflicto.id] === true,
        })),
      );
      setResultado(data);
      setPreview(null);
      setDecisiones({});
      setPopupConflictos(false);
      setAnioVista(data.anio);
      await cargarRanking(data.anio, tipo);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const cambiarVista = async (anio, tipoRanking) => {
    setError('');
    setAnioVista(anio);
    setTipo(tipoRanking);
    try {
      await cargarRanking(anio, tipoRanking);
    } catch (e) {
      setError(e.message);
    }
  };

  const quitar = async (carga) => {
    const nombre = carga.meet_nombre || carga.meet_id;
    if (!window.confirm(`Quitar ${nombre} del ranking ${carga.anio}? El resto del histórico se mantiene.`)) {
      return;
    }
    setError('');
    try {
      await quitarCargaRanking(carga.anio, carga.meet_id);
      await cargarRanking(carga.anio, tipo);
    } catch (e) {
      setError(e.message);
    }
  };

  const opciones = useMemo(() => {
    const grupos = ranking?.grupos || [];
    const unicos = (campo) => [...new Set(grupos.map((g) => g[campo]).filter(Boolean))];
    return {
      modalidad: unicos('modalidad'),
      genero: unicos('genero'),
      categoria_edad: unicos('categoria_edad'),
      tipo_competencia: unicos('tipo_competencia'),
      categoria_peso: unicos('categoria_peso'),
    };
  }, [ranking]);

  const gruposVisibles = useMemo(() => {
    const buscar = filtros.buscar.trim().toLowerCase();
    return (ranking?.grupos || []).filter((grupo) => {
      if (filtros.modalidad && grupo.modalidad !== filtros.modalidad) return false;
      if (filtros.genero && grupo.genero !== filtros.genero) return false;
      if (tipo === 'categoria' && filtros.categoria_edad && grupo.categoria_edad !== filtros.categoria_edad) return false;
      if (filtros.tipo_competencia && grupo.tipo_competencia !== filtros.tipo_competencia) return false;
      if (tipo === 'categoria' && filtros.categoria_peso && grupo.categoria_peso !== filtros.categoria_peso) {
        return false;
      }
      if (!buscar) return true;
      return grupo.atletas.some((a) => (a.nombre || '').toLowerCase().includes(buscar));
    }).map((grupo) => {
      const buscar = filtros.buscar.trim().toLowerCase();
      if (!buscar) return grupo;
      return {
        ...grupo,
        atletas: grupo.atletas.filter((a) => (a.nombre || '').toLowerCase().includes(buscar)),
      };
    });
  }, [ranking, filtros, tipo]);

  const anios = ranking?.anios?.length ? ranking.anios : [anioVista];
  const filtroSelect = { ...theme.select, width: 'auto', minWidth: 140 };
  const conflictos = preview?.conflictos || [];
  const pendientes = conflictos.filter((conflicto) => decisiones[conflicto.id] !== true && decisiones[conflicto.id] !== false);

  return (
    <div style={{ ...theme.container, maxWidth: '100%' }}>
      <div style={theme.pageHeader}>
        <div>
          <h1 style={theme.title}>Rankings</h1>
          <p style={theme.subtitle}>
            Carga un LiftingCast e indica el año del ranking. Una competencia puede contar para el
            año siguiente, y cada año queda guardado. El overall ordena por DOTS, sin separar por
            categoría de peso. El ranking por categoría ordena por la suma de sentadilla, banca y
            peso muerto. No entran intentos nulos, ni only bench, only deadlift u only squat.
            En un internacional solo entran los atletas de Chile.
          </p>
        </div>
      </div>

      <form onSubmit={previsualizar} style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
        <input
          type="text"
          value={url}
          onChange={(e) => cambiarUrl(e.target.value)}
          placeholder="https://archive.liftingcast.com/meets/.../board"
          style={theme.input}
          aria-label="URL de LiftingCast"
          disabled={cargando || guardando}
        />
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            Año del ranking
            <input
              type="number"
              min="1990"
              max="2100"
              value={anioCarga}
              onChange={(e) => {
                setAnioCarga(e.target.value);
                setPreview(null);
                setDecisiones({});
                setPopupConflictos(false);
                setResultado(null);
              }}
              style={{ ...theme.input, width: 110 }}
              aria-label="Año del ranking"
              disabled={cargando || guardando}
            />
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="radio"
              name="ambito-ranking"
              checked={ambito === 'nacional'}
              onChange={() => {
                setAmbito('nacional');
                setPreview(null);
                setDecisiones({});
                setPopupConflictos(false);
                setResultado(null);
              }}
            />
            Nacional
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="radio"
              name="ambito-ranking"
              checked={ambito === 'internacional'}
              onChange={() => {
                setAmbito('internacional');
                setPreview(null);
                setDecisiones({});
                setPopupConflictos(false);
                setResultado(null);
              }}
            />
            Internacional
          </label>
          <button type="submit" style={theme.btnPrimary} disabled={cargando || guardando || !url.trim() || !anioCarga}>
            {cargando ? 'Cargando...' : 'Cargar LiftingCast'}
          </button>
        </div>
      </form>

      {error && <div style={theme.error}>{error}</div>}

      {resultado && (
        <div style={{ ...theme.success, marginBottom: 24 }}>
          <div>{resultado.mensaje}</div>
          <div style={{ marginTop: 8 }}>
            {resultado.atletas} atletas · {resultado.resultados} resultados
            {resultado.meet?.nombre ? ` · ${resultado.meet.nombre}` : ''}
            {` · ranking ${resultado.anio}`}
          </div>
        </div>
      )}

      {preview && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h2 style={{ ...theme.title, fontSize: 20, margin: 0 }}>
                {preview.meet?.nombre || 'Previsualización'} · ranking {preview.anio}
              </h2>
              <p style={{ ...theme.subtitle, marginTop: 4 }}>
                {[preview.meet?.fecha_texto, preview.meet?.federacion].filter(Boolean).join(' · ')}
                {preview.meet?.ambito === 'internacional' || preview.meet?.ambito === 'ibero' ? ' · solo Chile' : ''}
                {` · ${preview.atletas} atletas entrarían (${preview.resultados} resultados)`}
                . Si este meet ya estaba en ese año, se reemplaza. Los otros años no se tocan.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {conflictos.length > 0 && (
                <button type="button" style={theme.btnSecondary} onClick={() => setPopupConflictos(true)}>
                  {pendientes.length
                    ? `Nombres parecidos (${pendientes.length})`
                    : 'Nombres parecidos resueltos'}
                </button>
              )}
              <button
                type="button"
                style={theme.btnPrimary}
                disabled={guardando || (preview.atletas || 0) === 0 || pendientes.length > 0}
                onClick={guardar}
              >
                {guardando ? 'Guardando...' : `Guardar en el ranking ${preview.anio}`}
              </button>
            </div>
          </div>
          <div style={{ overflow: 'auto', maxHeight: 480, border: '1px solid #eee', borderRadius: 8, marginBottom: 16 }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  {['Atleta', 'División', 'Modalidad', 'Género', 'Edad', 'Categoría', 'Sentadilla', 'Banca', 'Peso muerto', 'Total', 'DOTS'].map((h) => (
                    <th key={h} style={{ ...theme.th, position: 'sticky', top: 0, background: '#fff' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(preview.incluidos || []).length === 0 && (
                  <tr><td style={theme.td} colSpan={11}>Nadie de este meet entra al ranking.</td></tr>
                )}
                {(preview.incluidos || []).map((fila) => (
                  <tr key={`${fila.lifter_id}-${fila.division_id}`}>
                    <td style={theme.td}>{fila.nombre}</td>
                    <td style={theme.td}>{fila.division_nombre}</td>
                    <td style={theme.td}>{etiqueta(fila.modalidad)}</td>
                    <td style={theme.td}>{fila.genero}</td>
                    <td style={theme.td}>{etiqueta(fila.categoria_edad)}</td>
                    <td style={theme.td}>{fila.categoria_peso}</td>
                    <td style={theme.td}>{kg(fila.squat)}</td>
                    <td style={theme.td}>{kg(fila.bench_press)}</td>
                    <td style={theme.td}>{kg(fila.deadlift)}</td>
                    <td style={theme.td}>{kg(fila.total)}</td>
                    <td style={theme.td}>{dots(fila.dots)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(preview.excluidos || []).length > 0 && (
            <div>
              <h3 style={{ ...theme.title, fontSize: 16 }}>Quedan fuera</h3>
              <div style={{ overflow: 'auto', maxHeight: 240, border: '1px solid #eee', borderRadius: 8 }}>
                <table style={theme.table}>
                  <thead>
                    <tr>
                      {['Atleta', 'País', 'Motivo', 'Sentadilla', 'Banca', 'Peso muerto'].map((h) => (
                        <th key={h} style={theme.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.excluidos.map((fila) => (
                      <tr key={fila.lifter_id}>
                        <td style={theme.td}>{fila.nombre}</td>
                        <td style={theme.td}>{fila.pais || '—'}</td>
                        <td style={theme.td}>{MOTIVOS[fila.motivo] || fila.motivo}</td>
                        <td style={theme.td}>{kg(fila.squat)}</td>
                        <td style={theme.td}>{kg(fila.bench_press)}</td>
                        <td style={theme.td}>{kg(fila.deadlift)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <h2 style={{ ...theme.title, fontSize: 20, marginBottom: 8 }}>
        {tipo === 'overall' ? 'Ranking overall' : 'Ranking por categoría de peso'}
        {ranking?.anio ? ` ${ranking.anio}` : ''}
      </h2>
      <p style={{ ...theme.subtitle, marginBottom: 16 }}>
        {tipo === 'overall'
          ? 'Raw o classic, por género y tested/untested, sin separar por edad ni por categoría de peso. Single ply no entra. El puesto sale del DOTS, de mayor a menor.'
          : 'Además se separa por categoría de peso. El puesto sale del total, de mayor a menor.'}
        {' '}Si alguien compitió más de una vez en el año, queda el mejor resultado.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button
          type="button"
          style={tipo === 'overall' ? theme.btnPrimary : theme.btnSecondary}
          onClick={() => cambiarVista(anioVista, 'overall')}
        >
          Overall (DOTS)
        </button>
        <button
          type="button"
          style={tipo === 'categoria' ? theme.btnPrimary : theme.btnSecondary}
          onClick={() => cambiarVista(anioVista, 'categoria')}
        >
          Por categoría (total)
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <select
          value={anioVista}
          onChange={(e) => cambiarVista(Number(e.target.value), tipo)}
          style={filtroSelect}
          aria-label="Año del ranking guardado"
        >
          {anios.map((anio) => (
            <option key={anio} value={anio}>{anio}</option>
          ))}
        </select>
        <select
          value={filtros.modalidad}
          onChange={(e) => setFiltros({ ...filtros, modalidad: e.target.value })}
          style={filtroSelect}
          aria-label="Modalidad"
        >
          <option value="">Modalidad</option>
          {opciones.modalidad.map((v) => <option key={v} value={v}>{etiqueta(v)}</option>)}
        </select>
        <select
          value={filtros.genero}
          onChange={(e) => setFiltros({ ...filtros, genero: e.target.value })}
          style={filtroSelect}
          aria-label="Género"
        >
          <option value="">Género</option>
          {opciones.genero.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        {tipo === 'categoria' && (
          <select
            value={filtros.categoria_edad}
            onChange={(e) => setFiltros({ ...filtros, categoria_edad: e.target.value })}
            style={filtroSelect}
            aria-label="Categoría de edad"
          >
            <option value="">Edad</option>
            {opciones.categoria_edad.map((v) => <option key={v} value={v}>{etiqueta(v)}</option>)}
          </select>
        )}
        <select
          value={filtros.tipo_competencia}
          onChange={(e) => setFiltros({ ...filtros, tipo_competencia: e.target.value })}
          style={filtroSelect}
          aria-label="Tested o untested"
        >
          <option value="">Tested / Untested</option>
          {opciones.tipo_competencia.map((v) => <option key={v} value={v}>{etiqueta(v)}</option>)}
        </select>
        {tipo === 'categoria' && (
          <select
            value={filtros.categoria_peso}
            onChange={(e) => setFiltros({ ...filtros, categoria_peso: e.target.value })}
            style={filtroSelect}
            aria-label="Categoría de peso"
          >
            <option value="">Categoría de peso</option>
            {opciones.categoria_peso.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
        <input
          type="text"
          value={filtros.buscar}
          onChange={(e) => setFiltros({ ...filtros, buscar: e.target.value })}
          placeholder="Buscar atleta"
          style={{ ...theme.input, maxWidth: 220 }}
          aria-label="Buscar atleta en el ranking"
        />
      </div>

      {(ranking?.cargas || []).length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Competencias cargadas en {ranking.anio}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ranking.cargas.map((carga) => (
              <div key={carga.meet_id} style={{ ...theme.card, padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{carga.meet_nombre || carga.meet_id}</div>
                  <div style={{ color: '#777', fontSize: 12 }}>
                    {[carga.meet_fecha, `${carga.atletas} atletas`].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button type="button" style={theme.btnDanger} onClick={() => quitar(carga)}>Quitar</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {gruposVisibles.length === 0 && (
        <div style={theme.empty}>No hay resultados para este ranking.</div>
      )}

      {gruposVisibles.map((grupo) => (
        <div key={tituloGrupo(grupo, tipo)} style={{ marginBottom: 28 }}>
          <h3 style={{ ...theme.title, fontSize: 16 }}>{tituloGrupo(grupo, tipo)}</h3>
          <div style={{ overflow: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  {['Puesto', 'Atleta', 'Sentadilla', 'Banca', 'Peso muerto', 'Total', 'DOTS', 'Peso corporal', tipo === 'overall' ? 'Categoría' : null, 'Competencia'].filter(Boolean).map((h) => (
                    <th key={h} style={theme.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grupo.atletas.map((atleta) => (
                  <tr key={`${atleta.clave_atleta}-${atleta.puesto}`}>
                    <td style={{ ...theme.td, fontWeight: 700 }}>{atleta.puesto}</td>
                    <td style={theme.td}>{atleta.nombre}</td>
                    <td style={theme.td}>{kg(atleta.squat)}</td>
                    <td style={theme.td}>{kg(atleta.bench_press)}</td>
                    <td style={theme.td}>{kg(atleta.deadlift)}</td>
                    <td style={theme.td}>{kg(atleta.total)}</td>
                    <td style={theme.td}>{dots(atleta.dots)}</td>
                    <td style={theme.td}>{kg(atleta.peso_corporal)}</td>
                    {tipo === 'overall' && <td style={theme.td}>{atleta.categoria_peso}</td>}
                    <td style={theme.td}>
                      {atleta.meet_nombre || atleta.meet_id}
                      {atleta.meet_fecha ? (
                        <div style={{ color: '#777', fontSize: 12 }}>{atleta.meet_fecha}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {popupConflictos && conflictos.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="conflictos-titulo"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 760, width: '100%', maxHeight: '85vh', overflow: 'auto', padding: 20 }}>
            <h2 id="conflictos-titulo" style={{ ...theme.title, fontSize: 20 }}>Nombres parecidos</h2>
            <p style={theme.subtitle}>
              Indica si es la misma persona o si son personas distintas. Si es la misma, en el ranking del año queda una sola.
            </p>
            <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
              {conflictos.map((conflicto) => (
                <div key={conflicto.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                    {conflicto.motivo === 'mismo_nombre' ? 'Mismo nombre, datos distintos' : 'Nombres parecidos'}
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {conflicto.personas.map((persona) => (
                      <div key={persona.clave_atleta} style={{ flex: '1 1 220px', background: '#f7f7f7', borderRadius: 8, padding: 12 }}>
                        <div style={{ fontWeight: 700 }}>{persona.nombre}</div>
                        <div style={{ color: '#555', fontSize: 13, marginTop: 4 }}>
                          {persona.fecha_nacimiento || 'Sin fecha de nacimiento'}
                        </div>
                        <div style={{ color: '#555', fontSize: 13 }}>
                          {persona.origen === 'ranking' ? 'Ya está en el ranking' : 'En este meet'}
                          {persona.meet_nombre ? ` · ${persona.meet_nombre}` : ''}
                        </div>
                        {persona.peso_corporal != null && (
                          <div style={{ color: '#555', fontSize: 13 }}>{persona.peso_corporal} kg</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                    <button
                      type="button"
                      style={decisiones[conflicto.id] === true ? theme.btnPrimary : theme.btnSecondary}
                      onClick={() => setDecisiones({ ...decisiones, [conflicto.id]: true })}
                    >
                      Es la misma persona
                    </button>
                    <button
                      type="button"
                      style={decisiones[conflicto.id] === false ? theme.btnPrimary : theme.btnSecondary}
                      onClick={() => setDecisiones({ ...decisiones, [conflicto.id]: false })}
                    >
                      Son personas distintas
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ ...theme.btnRow, marginTop: 16 }}>
              <button type="button" style={theme.btnPrimary} onClick={() => setPopupConflictos(false)}>
                {pendientes.length ? 'Cerrar' : 'Listo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RankingsView;
